import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, List, ListItem, ListItemIcon,
    Divider, Box, Paper
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import CommentIcon from '@mui/icons-material/Comment';
import BookIcon from '@mui/icons-material/Book';
import BadgeIcon from '@mui/icons-material/Badge';
import HomeIcon from '@mui/icons-material/Home';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PublicIcon from '@mui/icons-material/Public';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import NumbersIcon from '@mui/icons-material/Numbers';
import ApartmentIcon from '@mui/icons-material/Apartment';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';

interface DraftField {
    name: string;
    value: any;
    rawValue?: any;
    isParticipants?: boolean;
    isEffects?: boolean;
    displayName?: string;
}

interface DraftSummary {
    draftDate: string;
    fields: DraftField[];
}

interface Props {
    open: boolean;
    onClose: () => void;
    onRestore: () => void;
    onDelete: () => void;
    draftSummary: DraftSummary;
}

export default function DraftRestoreDialog({
    open, onClose, onRestore, onDelete, draftSummary
}: Props) {
    // Function to render participants in a more readable format with the requested format
    const renderParticipants = (participants: any[]) => {
        return (
            <Box sx={{ mt: 1, width: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Uczestnicy:
                </Typography>
                {participants.map((participant, idx) => (
                    <Paper
                        key={idx}
                        variant="outlined"
                        sx={{ p: 1, mb: 1, width: '100%' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormatListNumberedIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {idx + 1}.
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ ml: 4 }}>
                            {participant.firstName || '-'} - {participant.lastName || '-'} - {participant.pesel || '-'} - {participant.birthPlace || '-'} - {participant.birthDate || '-'}
                        </Typography>
                    </Paper>
                ))}
            </Box>
        );
    };

    // Function to render education effects in numbered list format
    const renderEffects = (effects: string[]) => {
        return (
            <Box sx={{ mt: 1, width: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Efekty kształcenia:
                </Typography>
                {effects.map((effect, idx) => (
                    <Box key={idx} sx={{ display: 'flex', mb: 1, ml: 2 }}>
                        <FormatListNumberedIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2">
                            {idx + 1}. {effect}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    // Get the proper icon for a field
    const getFieldIcon = (fieldName: string) => {
        switch (fieldName) {
            case 'ID usługi': return <NumbersIcon fontSize="small" />;
            case 'Nazwa usługi': return <BusinessIcon fontSize="small" />;
            case 'Niestandardowa nazwa': return <BadgeIcon fontSize="small" />;
            case 'Proponowana data': return <CalendarTodayIcon fontSize="small" />;
            case 'Komentarz': return <CommentIcon fontSize="small" />;
            case 'Program szkolenia': return <BookIcon fontSize="small" />;
            case 'Efekty kształcenia': return <SchoolIcon fontSize="small" />;
            case 'Nazwa instytucji': return <ApartmentIcon fontSize="small" />;
            case 'Nazwisko płatnika': return <PersonIcon fontSize="small" />;
            case 'Imię płatnika': return <PersonIcon fontSize="small" />;
            case 'Ulica': return <HomeIcon fontSize="small" />;
            case 'Budynek': return <HomeIcon fontSize="small" />;
            case 'Nr lokalu': return <HomeIcon fontSize="small" />;
            case 'Kod pocztowy': return <LocationCityIcon fontSize="small" />;
            case 'Miasto': return <LocationCityIcon fontSize="small" />;
            case 'Województwo': return <LocationCityIcon fontSize="small" />;
            case 'Kraj': return <PublicIcon fontSize="small" />;
            case 'NIP': return <CreditCardIcon fontSize="small" />;
            case 'Uczestnicy': return <PersonIcon fontSize="small" />;
            default: return <BusinessIcon fontSize="small" />;
        }
    };

    // Order fields in the specific order requested
    const orderedFieldNames = [
        'ID usługi',
        'Nazwa usługi',
        'Niestandardowa nazwa',
        'Proponowana data',
        'Miejsce',
        'Komentarz',
        'Program szkolenia',
        'Efekty kształcenia',
        'Nazwa klienta',
        'Adres klienta',
        'NIP klienta',
        'Nazwa instytucji',
        'Nazwisko płatnika',
        'Imię płatnika',
        'Ulica',
        'Budynek',
        'Nr lokalu',
        'Kod pocztowy',
        'Miasto',
        'Województwo',
        'Kraj',
        'NIP',
        'Uczestnicy',
        'Liczba uczestników'
    ];

    // Map field names from API to display names
    const fieldNameMap: Record<string, string> = {
        'serviceId': 'ID usługi',
        'serviceName': 'Nazwa usługi',
        'customeClientServiceName': 'Niestandardowa nazwa',
        'preposedDateTime': 'Proponowana data',
        'placeOfServiceAndAnotherInformations': 'Miejsce',
        'clientComment': 'Komentarz',
        'trainingProgram': 'Program szkolenia',
        'educationEffects': 'Efekty kształcenia',
        'payerClientName': 'Nazwa klienta',
        'payerClientAddress': 'Adres klienta',
        'payerClientNIP': 'NIP klienta',
        'payerInstitutionName': 'Nazwa instytucji',
        'payerLastName': 'Nazwisko płatnika',
        'payerFirstName': 'Imię płatnika',
        'payerStreet': 'Ulica',
        'payerBuilding': 'Budynek',
        'payerPremises': 'Nr lokalu',
        'payerZip': 'Kod pocztowy',
        'payerCity': 'Miasto',
        'payerState': 'Województwo',
        'payerCountry': 'Kraj',
        'payerNIP': 'NIP',
        'rows': 'Uczestnicy',
        'totalItem': 'Liczba uczestników'
    };

    // Get the draftSummary fields and map them to our display names
    const fieldsWithDisplayNames = draftSummary.fields.map(field => {
        const displayName = fieldNameMap[field.name] || field.name;
        return { ...field, displayName };
    });

    // Sort fields according to our ordered list
    const sortedFields = [...fieldsWithDisplayNames].sort((a, b) => {
        const indexA = orderedFieldNames.indexOf(a.displayName);
        const indexB = orderedFieldNames.indexOf(b.displayName);

        // If both fields exist in our ordered list, sort by that order
        if (indexA >= 0 && indexB >= 0) {
            return indexA - indexB;
        }

        // If only one exists, prioritize it
        if (indexA >= 0) return -1;
        if (indexB >= 0) return 1;

        // Otherwise sort alphabetically
        return a.displayName.localeCompare(b.displayName);
    });

    // Function to format date to show only the date part
    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString();
        } catch {
            return dateString;
        }
    };

    // Function to render a field value properly
    const renderFieldValue = (field: DraftField) => {
        // For participants list
        if (field.displayName === 'Uczestnicy' && Array.isArray(field.rawValue)) {
            return renderParticipants(field.rawValue);
        }

        // For education effects
        if (field.displayName === 'Efekty kształcenia' && field.rawValue) {
            const effects = Array.isArray(field.rawValue)
                ? field.rawValue
                : typeof field.rawValue === 'string'
                    ? field.rawValue.split(';').filter(e => e.trim())
                    : [];

            return renderEffects(effects);
        }

        // Format date fields
        if (field.displayName === 'Proponowana data') {
            return (
                <Typography variant="body1">
                    {formatDate(String(field.value))}
                </Typography>
            );
        }

        // Default rendering
        return (
            <Typography variant="body1">
                {typeof field.value === 'object'
                    ? JSON.stringify(field.value)
                    : String(field.value)}
            </Typography>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="draft-restore-dialog-title"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle id="draft-restore-dialog-title">
                Przywracanie wersji roboczej formularza
            </DialogTitle>

            <DialogContent>
                <Typography variant="subtitle1" gutterBottom>
                    Znaleziono zapisaną wersję roboczą formularza z dnia {draftSummary.draftDate}.
                    Czy chcesz przywrócić poniższe dane?
                </Typography>

                <List sx={{ pt: 1 }}>
                    {sortedFields.map((field, index) => (
                        <React.Fragment key={field.displayName + index}>
                            <ListItem sx={{ py: 0.5, flexDirection: 'column', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        {getFieldIcon(field.displayName)}
                                    </ListItemIcon>
                                    <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                        {field.displayName}
                                    </Typography>
                                </Box>
                                <Box sx={{ ml: 4, width: '100%' }}>
                                    {renderFieldValue(field)}
                                </Box>
                            </ListItem>
                            {index < sortedFields.length - 1 && <Divider component="li" />}
                        </React.Fragment>
                    ))}
                </List>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Jeśli nie przywrócisz wersji roboczej, będzie ona nadal dostępna przez przycisk w prawej części ekranu.
                    Po rozpoczęciu edycji formularza, poprzednia wersja robocza zostanie automatycznie usunięta po 60 sekundach.
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onDelete} color="error" startIcon={<DeleteIcon />}>
                    Usuń wersję roboczą
                </Button>
                <div style={{ flexGrow: 1 }} />
                <Button onClick={onClose} color="primary" variant="outlined">
                    Nie teraz
                </Button>
                <Button
                    onClick={onRestore}
                    color="primary"
                    variant="contained"
                    startIcon={<RestoreIcon />}
                >
                    Przywróć
                </Button>
            </DialogActions>
        </Dialog>
    );
}